import { Box } from "@mui/material"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/system"
import AdminUserSelect from "components/AdminUserSelect"
import { DatePicker } from "components/common/DatePicker"
import { TimePicker } from "components/common/TimePicker"
import ScheduleMeetingDialogCloseIconButton from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogCloseIconButton"
import ScheduleMeetingDialogContainer from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogContainer"
import ScheduleMeetingDialogContent from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogContent"
import ScheduleMeetingDialogGoBackButton from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogGoBackButton"
import ScheduleMeetingDialogHeader from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogHeader"
import ScheduleMeetingDialogSingleBottomButton from "components/meetings/ScheduleMeetingDialog/ScheduleMeetingDialogSingleBottomButton"
import AdminUser from "models/convention-api/v1/AdminUser"
import ConventionAvailableMeetingTime from "models/convention-api/v1/ConventionAvailableMeetingTime"
import ConventionMeetingType from "models/convention-api/v1/ConventionMeetingType"
import moment from "moment-timezone"
import React, { useMemo, useState } from "react"
import { SWRResponse } from "swr"
import { AdminUserCard } from "components/AdminUserCard"

interface ScheduleMeetingDialogSelectTimeProps {
  onGoBack: () => void
  meetingTimes: SWRResponse<ConventionAvailableMeetingTime[], unknown>
  meetingType: ConventionMeetingType
  onContinue: (meetingAvailability: ConventionAvailableMeetingTime) => void
  onClose: () => void
}

export const ScheduleMeetingDialogSelectTime = (props: ScheduleMeetingDialogSelectTimeProps) => {
  const { onGoBack, meetingTimes, onContinue, onClose, meetingType } = props
  const [selectedDay, setSelectedDay] = useState<null | moment.Moment>(null)
  const [selectedAdminUser, setSelectedAdminUser] = useState<null | AdminUser>(null)
  const [selectedTime, setSelectedTime] = useState<null | moment.Moment>(null)

  // Business logic says that we should hide all available times that start in 15 minutes
  const minAvailableTime = moment().add(15, "minutes")
  const maxAvailableTime = moment().add(1, "years")

  const availableDays = useMemo(() => {
    if (!meetingTimes.data) return null

    const foundAvailableDays: Record<string, string> = {}

    meetingTimes.data.forEach((meetingTime) => {
      const { start_at } = meetingTime
      if (start_at.isBefore(minAvailableTime)) return

      const startKey = start_at.format("YYYY-MM-DD")
      if (foundAvailableDays[startKey]) return

      foundAvailableDays[startKey] = startKey
    })

    const availableDaysWithMoment = Object.values(foundAvailableDays).map((availableTime) =>
      moment(availableTime),
    )

    const firstAvailableDayWithMoment = availableDaysWithMoment[0]
    setSelectedDay(firstAvailableDayWithMoment)

    return availableDaysWithMoment
  }, [meetingTimes])

  const availableAdminUsers = useMemo(() => {
    if (!meetingTimes.data) return null
    if (!selectedDay) return null

    const foundAvailableAdminUsers: Record<string, AdminUser> = {}

    meetingTimes.data.forEach(({ meetingAvailability, start_at }) => {
      if (!meetingAvailability) return

      const { adminUser } = meetingAvailability
      if (!adminUser) return
      if (foundAvailableAdminUsers[adminUser.id]) return
      if (!selectedDay.isSame(start_at, "day")) return
      if (start_at.isBefore(minAvailableTime)) return

      foundAvailableAdminUsers[adminUser.id] = adminUser
    })

    const foundAvailableAdminUsersValues = Object.values(foundAvailableAdminUsers)

    const firstFoundAdminUser = foundAvailableAdminUsersValues[0]
    setSelectedAdminUser(firstFoundAdminUser)

    return foundAvailableAdminUsersValues
  }, [selectedDay])

  const availableTimes = useMemo(() => {
    if (!meetingTimes.data) return null
    if (!selectedAdminUser) return null
    if (!selectedDay) return null

    const foundMeetingTimes: ConventionAvailableMeetingTime[] = []

    meetingTimes.data.forEach((meetingTime) => {
      const { meetingAvailability } = meetingTime
      if (meetingAvailability?.admin_user_id !== selectedAdminUser.id) return
      if (meetingTime.start_at.isBefore(minAvailableTime)) return

      if (meetingTime.start_at.isSame(selectedDay, "day")) {
        foundMeetingTimes.push(meetingTime)
      }
    })

    if (!foundMeetingTimes.length) return []

    const firstFoundMeetingTime = foundMeetingTimes[0].start_at
    setSelectedTime(firstFoundMeetingTime)

    return foundMeetingTimes
  }, [selectedDay, selectedAdminUser])

  const availableTimeStartAts = useMemo(() => {
    if (!availableTimes) return null

    return availableTimes.map((availableTime) => availableTime.start_at)
  }, [availableTimes])

  const selectDate = (newSelectedDate: moment.Moment | null) => {
    setSelectedDay(newSelectedDate)
    setSelectedAdminUser(null)
    setSelectedTime(null)
  }

  const selectAdminUser = (adminUserId: string) => {
    setSelectedAdminUser(availableAdminUsers?.find((admin) => admin.id === adminUserId) ?? null)
    setSelectedTime(null)
  }

  const handleContinue = () => {
    if (!selectedTime) return console.error("selectedAvailableTime is null or undefined")
    if (!availableTimes) return console.error("availableTimes is null or empty")

    onContinue(
      availableTimes.find(({ start_at }) =>
        start_at.isSame(selectedTime),
      ) as ConventionAvailableMeetingTime,
    )
  }

  const isLoading = meetingTimes.data === undefined || meetingTimes.isValidating

  return (
    <>
      <ScheduleMeetingDialogHeader>
        <ScheduleMeetingDialogGoBackButton
          ariaLabel="Go back to selecting meeting type"
          onClick={onGoBack}
        />
        <ScheduleMeetingDialogCloseIconButton onClick={onClose} />
      </ScheduleMeetingDialogHeader>
      <ScheduleMeetingDialogContainer
        isLoading={isLoading}
        noDataText={`${meetingType.name} doesn't have any available dates`}
        hasNoData={availableDays?.length === 0}>
        <ScheduleMeetingDialogContent sx={{ marginBottom: "30px" }}>
          <_AccentText component="h3">
            Select a date, then select an available team member & time slot
          </_AccentText>
          <_SecondaryText component="p">
            We’ll only show you meetings scheduled to start at least 30 minutes from now.
          </_SecondaryText>
          <_SelectingDateContainer>
            {availableDays && (
              <DatePicker
                label="Date"
                selectedDate={selectedDay}
                onChange={selectDate}
                availableDates={availableDays}
                minDate={minAvailableTime}
                maxDate={maxAvailableTime}
              />
            )}

            {/*Prevent the user from selecting an admin user if there is only one admin user to select*/}
            {availableAdminUsers?.length <= 1 && selectedAdminUser ? (
                <AdminUserCard adminUser={selectedAdminUser} />
            ) : (
              <Box display="flex" flexDirection="column">
                <Typography variant="paragraphSmall">Select a Team Member</Typography>
                <AdminUserSelect
                  adminUsers={availableAdminUsers}
                  selectedAdminUserId={selectedAdminUser?.id ?? ""}
                  onSelectedAdminChange={(event) => selectAdminUser(event.target.value as string)}
                />
              </Box>
            )}

            {availableTimeStartAts && (
              <TimePicker
                key={selectedAdminUser?.id}
                Title={
                  <_TimePickerTitle component="p">
                    Available Times ({availableTimeStartAts[0].format("z")})
                  </_TimePickerTitle>
                }
                availableTimes={availableTimeStartAts}
                selected={selectedTime}
                onSelect={setSelectedTime}
              />
            )}
          </_SelectingDateContainer>
        </ScheduleMeetingDialogContent>
        <ScheduleMeetingDialogSingleBottomButton label="Continue" onClick={handleContinue} />
      </ScheduleMeetingDialogContainer>
    </>
  )
}

const _SelectingDateContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr",
  gridGap: "24px",
})

const _AccentText = styled(Typography)(({ theme }) => ({
  marginTop: "10px",
  marginBottom: "10px",

  ...theme.fonts.lato400,
  fontSize: 20,
  lineHeight: 1.2,
})) as typeof Typography

const _SecondaryText = styled(Typography)(({ theme }) => ({
  marginBottom: "22px",
  color: theme.palette.gray.dark,
  fontStyle: "italic",
})) as typeof Typography

const _TimePickerTitle = styled(Typography)(({ theme }) => ({
  ...theme.fonts.lato400,
  fontSize: 14,
  fontWeight: "bold",
  lineHeight: 1.6,

  marginBottom: 10,
})) as typeof Typography
